/**
 * AffiliateAuthContext
 *
 * Manages the affiliate JWT lifecycle for the affiliate portal. Stored in
 * localStorage under a separate key from the main app's user token so the
 * two never conflict.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { affiliateApi, AffiliateApiError } from '../services/affiliateApi';
import { AffiliateProfile } from '../types/affiliate';

interface AffiliateAuthContextValue {
  affiliate: AffiliateProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  /** Resolves with the signed-in profile so callers can route on `status` without waiting a render. */
  signIn: (token: string) => Promise<AffiliateProfile | null>;
  signOut: () => void;
  refresh: () => Promise<AffiliateProfile | null>;
}

const AffiliateAuthContext = createContext<AffiliateAuthContextValue | null>(null);

export const AffiliateAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [affiliate, setAffiliate] = useState<AffiliateProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (): Promise<AffiliateProfile | null> => {
    if (!affiliateApi.getToken()) {
      setAffiliate(null);
      setLoading(false);
      return null;
    }
    try {
      const { affiliate } = await affiliateApi.getMe();
      setAffiliate(affiliate);
      return affiliate;
    } catch (err) {
      // 401 → token expired/invalid; clear silently
      if (err instanceof AffiliateApiError && err.status === 401) {
        affiliateApi.clearToken();
      }
      setAffiliate(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(
    async (token: string): Promise<AffiliateProfile | null> => {
      affiliateApi.setToken(token);
      setLoading(true);
      return refresh();
    },
    [refresh]
  );

  const signOut = useCallback(() => {
    affiliateApi.clearToken();
    setAffiliate(null);
  }, []);

  return (
    <AffiliateAuthContext.Provider
      value={{
        affiliate,
        loading,
        isAuthenticated: !!affiliate,
        signIn,
        signOut,
        refresh,
      }}
    >
      {children}
    </AffiliateAuthContext.Provider>
  );
};

export const useAffiliateAuth = (): AffiliateAuthContextValue => {
  const ctx = useContext(AffiliateAuthContext);
  if (!ctx) {
    throw new Error('useAffiliateAuth must be used within AffiliateAuthProvider');
  }
  return ctx;
};
